// @/utils/pdfQuotationBase.js

/**
 * PDFQuotationBase
 * รวมเมธอด/ฟังก์ชันที่ใช้ร่วมกันระหว่าง Fixed Price Quotation และ Fee Estimate
 * (header บริษัท, ข้อมูลลูกค้า, กรอบตารางแบบ absolutePosition ฯลฯ)
 *
 * ตัว generator เฉพาะแต่ละประเภทเอกสาร:
 *   - pdfQuotationFixedGenerator.js    (doc_type === "fixed")
 *   - pdfQuotationEstimateGenerator.js (doc_type === "estimate")
 * จะ extends คลาสนี้ แล้วเติมเฉพาะส่วนที่ต่างกัน (โครงตาราง/แถวสรุปยอด/เงื่อนไขท้ายเอกสาร)
 */
export default class PDFQuotationBase {
    /**
     * รอให้ pdfMake พร้อมใช้งาน (โหลดฟอนต์ภาษาไทยเสร็จ)
     * @returns {Promise<boolean>}
     */
    waitForPdfMakeReady() {
        return new Promise((resolve, reject) => {
            const checkInterval = setInterval(() => {
                if (typeof pdfMake !== "undefined" && pdfMake.vfs && pdfMake.fonts) {
                    clearInterval(checkInterval);
                    resolve(true);
                }
            }, 100);

            setTimeout(() => {
                clearInterval(checkInterval);
                reject(new Error("pdfMake โหลดไม่สำเร็จ"));
            }, 10000);
        });
    }

    formatNumber(value) {
        if (!value && value !== 0) return "0.00";
        return new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    }

    formatDate(dateString) {
        if (!dateString) return "-";
        if (dateString.includes("/")) return dateString;

        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        const day = date.getDate().toString().padStart(2, "0");
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }

    /**
     * เติมค่า default ของบริษัทให้ header
     */
    buildCompanyHeader(headerInput) {
        const header = { ...(headerInput || {}) };

        if (["D", "L"].includes(header.company_inv)) {
            header.com_eng_1 = header.com_eng_1 || "DEJ-UDOM & ASSOCIATES LTD.";
            header.com_eng_2 = header.com_eng_2 || "ATTORNEYS AT LAW";
            header.com_thai = header.com_thai || "บริษัท เดชอุดม แอนด์ แอสโซซิเอทส์ จำกัด";
            header.com_add_name = header.com_add_name || "CHARN ISSARA TOWER 1, 2nd FLOOR,";
            header.com_add_no = header.com_add_no || "942/69 RAMA IV ROAD,";
            header.com_add_city = header.com_add_city || "KWAENG SURIYAWONG, KHET BANGRAK,";
            header.com_add_country = header.com_add_country || "BANGKOK 10500, THAILAND";
            header.com_add_tel = header.com_add_tel || "Tel : 0-2233-0055, 0-2267-5152";
            header.com_add_email = header.com_add_email || "E-MAIL : account@dejudom.com";
            header.com_add_tax_id = header.com_add_tax_id || 'TAX ID. No. 0105534015348/"HEAD OFFICE"';
        } else {
            header.com_eng_1 = header.com_name || "ไม่มีข้อมูลบริษัท";
            header.com_eng_2 = header.com_eng_2 || "";
            header.com_thai = header.inv_company_name_th || "ไม่มีข้อมูลบริษัท";
            header.com_add_name = header.inv_company_address ? `${header.inv_company_address},` : " ";
            header.com_add_no =
                header.inv_company_address2 && header.inv_company_address2 !== ""
                    ? `${header.inv_company_address2},`
                    : " ";
            header.com_add_city = `${header.inv_subdistrict || ""}, ${header.inv_district || ""},`;
            header.com_add_country = `${header.inv_province || ""}, ${header.inv_postcode || ""}, ${header.inv_country || ""
                }`;
            header.com_add_tel = `Tel : ${header.inv_company_tel || "-"}`;
            header.com_add_email = `E-MAIL : ${header.inv_company_email || "-"}`;
            header.com_add_tax_id = `TAX ID. No. ${header.inv_tax_id || "-"}/"${header.inv_company_branch || "-"}"`;
        }

        return header;
    }

    generateCompanyHeaderBlock(header) {
        return {
            columns: [
                {
                    width: "50%",
                    stack: [
                        { text: header.com_eng_1, fontSize: 16, bold: true, margin: [0, 0, 0, 1] },
                        { text: header.com_eng_2, fontSize: 9, margin: [0, 0, 0, 2] },
                        { text: header.com_thai, fontSize: 11, margin: [0, 0, 0, 1] },
                    ],
                },
                {
                    width: "50%",
                    stack: [
                        { text: header.com_add_name, fontSize: 8, alignment: "right" },
                        { text: header.com_add_no, fontSize: 8, alignment: "right" },
                        { text: header.com_add_city, fontSize: 8, alignment: "right" },
                        { text: header.com_add_country, fontSize: 8, alignment: "right" },
                        { text: header.com_add_tel, fontSize: 8, alignment: "right", margin: [0, 2, 0, 0] },
                        { text: header.com_add_email, fontSize: 8, alignment: "right" },
                        { text: header.com_add_tax_id, fontSize: 8, alignment: "right", bold: true },
                    ],
                },
            ],
            margin: [0, 0, 0, 15],
        };
    }

    generateTitleBlock(header, docTitle) {
        return {
            columns: [
                { width: "60%", text: docTitle, fontSize: 18, bold: true },
                {
                    width: "40%",
                    text: `${header.doc_title_en || "Quotation No"} / ${header.doc_title_th || "เลขที่ใบเสนอราคา"}: ${header.doc_no || ""
                        }`,
                    fontSize: 10,
                    alignment: "right",
                    margin: [0, 6, 0, 0],
                },
            ],
            margin: [0, 0, 0, 5],
        };
    }

    generateDividerLine() {
        return {
            canvas: [{ type: "line", x1: 0, y1: 0, x2: 545, y2: 0, lineWidth: 1 }],
            margin: [0, 0, 0, 10],
        };
    }

    /**
     * ประเมินความสูงจริงของ generateClientInfoBlock ตามจำนวนบรรทัดที่อยู่ลูกค้าจริง
     */
    estimateClientInfoHeight(header) {
        const addressLines = Array.isArray(header.customer_address)
            ? header.customer_address
            : (header.customer_address || "").split("\n");
        const addressLineCount = Math.max(addressLines.length, 1);

        const fixedPart = 16 + 16 + 13 + 12;
        const addressPart = addressLineCount * 13 + 6;

        return fixedPart + addressPart;
    }

    generateClientInfoBlock(header) {
        const addressLines = Array.isArray(header.customer_address)
            ? header.customer_address
            : (header.customer_address || "").split("\n");

        return {
            columns: [
                {
                    width: "58%",
                    stack: [
                        {
                            text: [
                                { text: "To / ถึง: ", fontSize: 10 },
                                { text: header.customer_name || "", fontSize: 10, bold: true },
                            ],
                            margin: [0, 0, 0, 2],
                        },
                        { text: addressLines, fontSize: 9, margin: [0, 0, 0, 5] },
                        { text: `Tax ID: ${header.customer_taxid || "-"}`, fontSize: 9, margin: [0, 3, 0, 2] },
                        { text: `Attention : ${header.attention || header.customer_contact_name || "-"}`, fontSize: 9 },
                    ],
                },
                {
                    width: "42%",
                    stack: [
                        {
                            text: `Date / วันที่: ${this.formatDate(header.doc_date)}`,
                            fontSize: 9,
                            alignment: "right",
                            margin: [0, 0, 0, 4],
                        },
                        {
                            text: `Credit Term / กำหนดชำระ: ${header.payment_term || "0"} Days`,
                            fontSize: 9,
                            alignment: "right",
                        },
                    ],
                },
            ],
            margin: [0, 0, 0, 10],
        };
    }

    /**
     * แปลง items ให้เป็น table body ของ pdfMake — ใช้ร่วมกันทั้ง 2 ประเภทเอกสาร
     * Fixed Price: item.amount (ตัวเลข) -> โชว์เป็นจำนวนเงินจัดรูปแบบ
     * Fee Estimate: item.rate (string เช่น "30,000.00 - 50,000.00") -> โชว์ตรงๆ
     */
    buildItemRows(items) {
        return (items || []).map((item) => {
            const descStack = [{ text: item.description || item.name || "", fontSize: 9, bold: !!item.bold }];
            if (item.detail) {
                descStack.push({
                    text: item.detail,
                    fontSize: 8,
                    italics: true,
                    color: item.bold ? "#000000" : "#1565C0",
                    margin: [0, 2, 0, 0],
                });
            }

            const amountText =
                item.amount !== undefined && item.amount !== null ? this.formatNumber(item.amount) : item.rate || "";

            return [
                { stack: descStack, margin: [5, 4, 5, 4] },
                { text: amountText, fontSize: 9, bold: !!item.bold, alignment: "right", margin: [5, 4, 5, 4] },
            ];
        });
    }

    /**
     * ความสูงต่อแถวของ buildItemRows โดยนับจำนวนบรรทัดของ detail ด้วย
     */
    buildRowHeights(items) {
        return (items || []).map((item) => {
            if (!item.detail) return 22;
            const detailLineCount = String(item.detail).split("\n").length;
            return 22 + detailLineCount * 12;
        });
    }

    /**
     * บล็อกเงื่อนไขท้ายเอกสาร รองรับทั้งย่อหน้าเดียว (string) และหลายย่อหน้า (array)
     * — Fee Estimate มีเงื่อนไข 3 ย่อหน้าแยกกัน จึงต้องเว้นระยะห่างระหว่างย่อหน้า
     * @param {string|string[]} termsText
     */
    generateTermsBlock(termsText) {
        const paragraphs = Array.isArray(termsText) ? termsText : [termsText];
        return {
            stack: [
                { text: "Quotation Terms", fontSize: 8, bold: true, color: "red", margin: [0, 6, 0, 2] },
                ...paragraphs.map((p, idx) => ({
                    text: p,
                    fontSize: 7,
                    bold: true,
                    color: "red",
                    alignment: "justify",
                    margin: idx < paragraphs.length - 1 ? [0, 0, 0, 8] : [0, 0, 0, 0],
                })),
            ],
        };
    }

    /**
     * ตารางรายการแบบมีกรอบ วางตำแหน่งคงที่ (absolutePosition) เพื่อให้ขอบล่างของกรอบ
     * ตรงกับจุดเริ่ม footer เป๊ะเสมอ ไม่ว่าความสูงของ header/title/client info ด้านบน
     * จะประมาณคลาดเคลื่อนไปบ้างก็ตาม — ใช้ร่วมกันทั้ง Fixed Price และ Fee Estimate
     *
     * ⚠️ ข้อจำกัด: วางด้วย absolutePosition จึงไม่ไหลข้ามหน้าอัตโนมัติ ถ้ารายการมาก
     * จนเกินพื้นที่กรอบ แถวส่วนเกินจะล้นออกนอกกรอบ/ทับ footer ได้ — เหมาะกับใบเสนอราคา
     * ทั่วไป (ไม่เกิน ~15-20 รายการ)
     *
     * @param {Object} opts
     * @param {string} opts.col1Header ข้อความหัวคอลัมน์ซ้าย
     * @param {string} opts.col2Header ข้อความหัวคอลัมน์ขวา
     * @param {Array} opts.bodyRows แถวเนื้อหา [[cellซ้าย, cellขวา], ...]
     * @param {Array<number>} opts.bodyHeights ความสูงแต่ละแถวใน bodyRows (ลำดับตรงกัน)
     * @param {Array} opts.extraRows แถวสรุปยอด/ท้ายตาราง (ต่อในตารางเดียวกัน กันเส้นซ้อน)
     * @param {Array<number>} opts.extraHeights ความสูงแต่ละแถวใน extraRows
     * @param {number} opts.startY ตำแหน่ง y เริ่มต้นของกรอบ
     * @param {number} opts.bottomY ตำแหน่ง y ของขอบล่างกรอบ (จุดเริ่ม footer)
     */
    renderBoxedTable({ col1Header, col2Header, bodyRows, bodyHeights, extraRows, extraHeights, startY, bottomY }) {
        const HEADER_ROW_HEIGHT = 26;
        const usedHeight =
            HEADER_ROW_HEIGHT +
            bodyHeights.reduce((s, h) => s + h, 0) +
            extraHeights.reduce((s, h) => s + h, 0);

        const boxHeight = Math.max(bottomY - startY, HEADER_ROW_HEIGHT);
        const fillerHeight = Math.max(boxHeight - usedHeight, 0);

        const blankRow = [
            { text: "", margin: [5, 3, 5, 3] },
            { text: "", margin: [5, 3, 5, 3] },
        ];

        const body = [
            [
                { text: col1Header, fontSize: 10, bold: true, margin: [5, 5, 5, 5] },
                { text: col2Header, fontSize: 10, bold: true, alignment: "right", margin: [5, 5, 5, 5] },
            ],
            ...bodyRows,
            blankRow,
            ...extraRows,
        ];

        const firstExtraRowIndex = body.length - extraRows.length;
        // ความสูงเป็นตัวเลขคงที่ทุกแถว (ไม่ใช้ "auto") ผลรวม = boxHeight เป๊ะ
        const heights = [HEADER_ROW_HEIGHT, ...bodyHeights, fillerHeight, ...extraHeights];

        return {
            absolutePosition: { x: 25, y: startY },
            table: {
                headerRows: 1,
                widths: [423, 120],
                body,
                heights,
            },
            layout: {
                hLineWidth: (i) => {
                    if (i === 0 || i === 1) return 1;
                    if (extraRows.length > 0 && i >= firstExtraRowIndex) return 1;
                    return 0;
                },
                vLineWidth: () => 1,
                hLineColor: () => "black",
                vLineColor: () => "black",
                paddingLeft: () => 0,
                paddingRight: () => 0,
                paddingTop: () => 0,
                paddingBottom: () => 0,
            },
        };
    }
}